<template>
    <BaseDialog v-model:open="open" title="Share your Profile" title-style="serif" :description="sharingDescription" :reserve-mobile-nav-space="!recipientName">
        <p class="body-small text-ink-muted">{{ sharingDescription }}</p>
        <BaseNotification v-if="teamsEnabled" theme="info" hide-dismiss :text="t('collaborationProfile.sharing.teamAccess')" />

        <div class="flex items-center justify-between gap-4" data-testid="general-access-section">
            <div class="flex items-center gap-2">
                <span class="body-semibold text-ink">General Access</span>
                <LoadingSpinner v-if="isAccessChanging" class="text-ink-muted w-4 h-4" />
                <i v-else-if="isShowingAccepted" class="bi bi-check text-status-success text-xl/none" aria-hidden="true"></i>
            </div>
            <div class="shrink-0">
                <BaseSelect
                    v-model="localAccess"
                    :disabled="isButtonDisabled"
                    :options="accessOptions"
                    aria-label="General access"
                    @update:model-value="handleChangeProfileAccess" />
            </div>
        </div>

        <HorizontalDivider />

        <div>
            <div class="flex flex-col md:h-full">
                <template v-if="isOrgAccessLevel || isInviteOnlyAccessLevel">
                    <BaseCard v-if="isOrgAccessLevel" theme="muted" padding="sm" data-testid="organization-access-message">
                        <div class="flex gap-3">
                            <BaseAvatar shape="square" aria-hidden="true">
                                <OrganizationIcon class="h-6" aria-hidden="true" />
                            </BaseAvatar>
                            <div class="min-w-0">
                                <p class="body-small-bold text-ink">Visible to your organization</p>
                                <p class="body-small text-ink-muted mt-1">Everyone in your organization can find your profile and see how you work best.</p>
                            </div>
                        </div>
                    </BaseCard>

                    <template v-else>
                        <div v-if="showInvitationSkeletons" class="flex flex-col gap-4 grow">
                            <h3 class="body-bold text-ink-muted mb-1">{{ t("collaborationProfile.sharing.invitedPeople") }}</h3>
                            <div v-for="n in 2" :key="n" class="flex items-center gap-3">
                                <SkeletonLoader class="size-10 shrink-0 rounded-full" aria-label="Loading user" />
                                <div class="flex-1 flex flex-col gap-2">
                                    <SkeletonLoader class="h-3.5 w-48 rounded" aria-label="Loading name" />
                                    <SkeletonLoader class="h-3 w-40 rounded" aria-label="Loading status" />
                                </div>
                            </div>
                        </div>
                        <div
                            v-else-if="activeAcceptances.length === 0"
                            class="flex flex-col grow justify-center items-center text-center py-2 gap-2"
                            data-testid="no-acceptances-message">
                            <i class="bi bi-people text-2xl text-ink-muted" aria-hidden="true"></i>
                            <p class="body-small text-ink-muted">No one has accepted your invite yet. Share your link to get started!</p>
                        </div>
                        <div v-else class="grow">
                            <h3 class="body-bold text-ink-muted mb-3">{{ t("collaborationProfile.sharing.invitedPeople") }}</h3>
                            <div class="flex flex-col gap-4">
                                <div v-for="acceptance in activeAcceptances" :key="acceptance.id" class="flex items-center gap-3">
                                    <PeopleAvatar :name="acceptance.user_name || acceptance.user_email" />
                                    <div class="min-w-0 flex-1">
                                        <div class="body-small-semibold text-ink truncate">
                                            {{ acceptance.user_name || acceptance.user_email }}
                                        </div>
                                        <div class="body-small text-ink-muted">Connected&nbsp;<BaseDate :value="acceptance.accepted_at" format="relative" /></div>
                                    </div>
                                    <BaseDropdownMenu :sections="acceptanceMenuSections" :side-offset="8" @select="(item) => handleAcceptanceMenuSelect(item, acceptance.id)">
                                        <template #trigger>
                                            <BaseButton theme="tertiary" size="sm">
                                                Connected
                                                <i class="bi bi-chevron-down text-xs" aria-hidden="true"></i>
                                            </BaseButton>
                                        </template>
                                    </BaseDropdownMenu>
                                </div>
                            </div>
                        </div>
                    </template>

                    <BaseCard v-if="isOrgAccessLevel" padding="sm" class="mt-3">
                        <p class="body-small-bold text-ink">Included message</p>
                        <p class="body-small text-ink-muted mt-1">{{ includedMessage }}</p>
                    </BaseCard>
                </template>
                <template v-else-if="isPrivateAccessLevel">
                    <BaseCard theme="muted" padding="lg" class="mt-1 md:h-full">
                        <div class="flex h-full flex-col items-center justify-center gap-3 text-center">
                            <i class="bi bi-lock text-2xl text-ink-muted" aria-hidden="true"></i>
                            <p class="body-bold text-ink">Your profile is private</p>
                            <p class="body-small text-ink-muted">
                                <template v-if="recipientName && availableAccessLevels.has(ACCESS.ORGANIZATION)">
                                    {{ t("collaborationProfile.sharing.privateOrganization", { name: recipientName }) }}
                                </template>
                                <template v-else-if="recipientName">{{ t("collaborationProfile.sharing.privateInvitation", { name: recipientName }) }}</template>
                                <template v-else>{{ t("collaborationProfile.sharing.privateDescription") }}</template>
                            </p>
                        </div>
                    </BaseCard>
                </template>
            </div>
        </div>
        <BaseNotification v-if="accessChangeError" theme="error" hide-dismiss class="mt-2" :text="accessChangeErrorMessage" />
        <BaseNotification v-if="shareLinkError" theme="error" hide-dismiss class="mt-2" :text="String(shareLinkError)" />
        <template v-if="isOrgAccessLevel || isInviteOnlyAccessLevel" #footer>
            <BaseButton v-if="recipientName" theme="primary" :disabled="isButtonDisabled" @click="handleShareBack">Share</BaseButton>
            <div v-else class="grid w-full grid-cols-2 gap-3" data-testid="share-via-section">
                <BaseButton theme="primary" class="col-span-2" :disabled="isButtonDisabled" @click="handleCopyShareLink">
                    <i class="bi" :class="isCopied ? 'bi-check-lg' : 'bi-link-45deg'" aria-hidden="true"></i>
                    {{ isCopied ? "Copied!" : "Copy Link" }}
                </BaseButton>
                <BaseButton v-if="orgHasSlack" theme="neutral" :disabled="isButtonDisabled" @click="handleShareViaSlack">Share via Slack</BaseButton>
                <BaseButton v-else-if="orgHasMsTeams" theme="neutral" :disabled="isButtonDisabled" @click="handleShareViaTeams">Share via Teams</BaseButton>
                <BaseButton theme="neutral" :class="{ 'col-span-2': !hasChatShareOption }" :disabled="isButtonDisabled" @click="handleShareViaEmail">Share via Email</BaseButton>
            </div>
        </template>
    </BaseDialog>
    <ConfirmationDialog
        v-model:open="isReduceAccessConfirmOpen"
        :title="reduceAccessConfirmation.title"
        :text="reduceAccessConfirmation.text"
        confirm-label="Change access"
        confirm-theme="danger"
        container-class="md:w-120"
        @confirm="confirmReduceAccess"
        @cancel="cancelReduceAccess" />
</template>

<script setup lang="ts">
import { router } from "@inertiajs/vue3";
import { useTimeoutFn } from "@vueuse/core";
import { useFetch } from "~vue/api";
import type { ProfileAcceptanceSummary, ShareInvitationList } from "~vue/api/generated/collab-profiles";
import { PROFILE_SHARE_MESSAGE, profileShareBackMessage, type ProfileShareTarget, useProfileShareActions } from "~vue/CollaborationProfile/composables/useProfileShareActions";
import { useProfileSharingCopy } from "~vue/CollaborationProfile/composables/useProfileSharingCopy";
import { logProfileShared, type ProfileShareSource } from "~vue/CollaborationProfile/profileShareAttribution";
import BaseAvatar from "~vue/components/BaseAvatar.vue";
import BaseButton from "~vue/components/BaseButton.vue";
import BaseCard from "~vue/components/BaseCard.vue";
import BaseDate from "~vue/components/BaseDate.vue";
import BaseDialog from "~vue/components/BaseDialog.vue";
import BaseDropdownMenu, { type DropdownMenuItem, type DropdownMenuSection } from "~vue/components/BaseDropdownMenu.vue";
import BaseNotification from "~vue/components/BaseNotification.vue";
import BaseSelect from "~vue/components/BaseSelect.vue";
import ConfirmationDialog from "~vue/components/ConfirmationDialog.vue";
import HorizontalDivider from "~vue/components/HorizontalDivider.vue";
import PeopleAvatar from "~vue/components/PeopleAvatar.vue";
import SkeletonLoader from "~vue/components/SkeletonLoader.vue";
import { useTimedShow } from "~vue/composables/useTimedShow";
import LoadingSpinner from "~vue/icons/LoadingSpinner.vue";
import OrganizationIcon from "~vue/icons/OrganizationIcon.vue";
import type { ExistingPublicProfile, ProfilePayload } from "~vue/types/profile";
import AmplitudeEventNames from "~vue/utils/amplitudeEventNames";
import { ACCESS } from "~vue/utils/constants";
import { logUserInteraction } from "~vue/utils/logUtils";
import { computed, ref, watch } from "vue";
import { useI18n } from "vue-i18n";

const { t } = useI18n();
const { teamsEnabled } = useProfileSharingCopy();

type ShareProfileDialogProps = {
    existingPublicProfile: ExistingPublicProfile;
    orgHasMsTeams?: boolean;
    orgHasSlack?: boolean;
    availableAccessLevels: Set<string>;
    ownProfileUrl?: string;
    recipientName?: string;
    shareSource?: ProfileShareSource;
};

const {
    existingPublicProfile,
    orgHasMsTeams = false,
    orgHasSlack = false,
    availableAccessLevels,
    ownProfileUrl,
    recipientName,
    shareSource = "profile_page",
} = defineProps<ShareProfileDialogProps>();
const emit = defineEmits<{ shared: [target: ProfileShareTarget] }>();

const profileId = computed(() => existingPublicProfile.id);

const open = defineModel<boolean>("open", { default: false });

const localAccess = ref(existingPublicProfile.access ?? ACCESS.PRIVATE);

const accessOptions = computed(() => {
    const allOptions = [
        { key: ACCESS.PRIVATE, label: "Private" },
        { key: ACCESS.INVITED_ORG_MEMBERS, label: "Invited Only" },
        { key: ACCESS.ORGANIZATION, label: "Organization" },
    ];
    return allOptions.filter((o) => availableAccessLevels.has(o.key));
});

const hasChatShareOption = computed(() => orgHasSlack || orgHasMsTeams);

const isOrgAccessLevel = computed(() => localAccess.value === ACCESS.ORGANIZATION);
const isInviteOnlyAccessLevel = computed(() => localAccess.value === ACCESS.INVITED_ORG_MEMBERS);
const isPrivateAccessLevel = computed(() => localAccess.value === ACCESS.PRIVATE);
const sharingDescription = computed(() =>
    recipientName
        ? `Share your Collaboration Profile, including your Perspective results when available, with ${recipientName}.`
        : "Share how you work best, including your Perspective results when available.",
);

const invitationsUrl = computed(() => (profileId.value ? `/v1/profiles/${profileId.value}/shared-invitations` : ""));

const {
    data: invitations,
    error: invitationLoadError,
    isFetching: isFetchingAcceptances,
    execute: fetchInvitations,
} = useFetch(invitationsUrl, { immediate: false, refetch: false }).json<ShareInvitationList>();

const showInvitationSkeletons = computed(() => isFetchingAcceptances.value && invitations.value === null);
const ensureInvitationsLoaded = () => {
    if (!profileId.value || isFetchingAcceptances.value) {
        return;
    }
    if (invitations.value === null || invitationLoadError.value) {
        void fetchInvitations();
    }
};

const activeAcceptances = computed<ProfileAcceptanceSummary[]>(() => invitations.value?.acceptances?.filter((a) => a.revoked_at === null) ?? []);

const revokeUrl = ref("");
// The revoke endpoint returns 204 No Content: .json() would throw on the empty body, and useFetch
// parses before checking response.ok, so every successful revoke logged an error to Sentry.
const { execute: executeRevoke } = useFetch(revokeUrl, { immediate: false }).delete();

const acceptanceMenuSections = computed<DropdownMenuSection[]>(() => [
    { id: "access", items: [{ id: "revoke", type: "button", label: t("collaborationProfile.sharing.removeInvitation"), theme: "danger" }] },
]);

const handleAcceptanceMenuSelect = (item: DropdownMenuItem, acceptanceId: string) => {
    if (item.id === "revoke") {
        void handleRevoke(acceptanceId);
    }
};

const handleRevoke = async (acceptanceId: string) => {
    if (!profileId.value || !isInviteOnlyAccessLevel.value) {
        return;
    }
    revokeUrl.value = `/v1/profiles/${profileId.value}/acceptances/${acceptanceId}`;
    await executeRevoke();
    await fetchInvitations();
};

const invitationShareUrl = computed(() => invitations.value?.active_link?.share_url);

const { isGeneratingLink, shareLinkError, isCopied, copyLink, shareViaSlack, shareViaTeams, shareViaEmail } = useProfileShareActions({
    profileId,
    access: localAccess,
    activeShareUrl: invitationShareUrl,
    ownProfileUrl: computed(() => ownProfileUrl),
    shareSource,
});

const trackShare = (target: ProfileShareTarget) => {
    void logProfileShared({ shareSource, target, shareKind: "own_profile", access: localAccess.value });
};

const includedMessage = computed(() => (recipientName ? profileShareBackMessage(recipientName) : PROFILE_SHARE_MESSAGE));

const handleCopyShareLink = async () => {
    if (await copyLink(includedMessage.value)) {
        trackShare("link");
        emit("shared", "link");
    }
};

const handleShareBack = async () => {
    if (orgHasMsTeams) {
        await handleShareViaTeams();
    } else {
        await handleCopyShareLink();
    }
};

const handleShareViaSlack = async () => {
    if (await shareViaSlack()) {
        trackShare("slack");
    }
};

const handleShareViaTeams = async () => {
    if (await shareViaTeams(includedMessage.value)) {
        trackShare("teams");
        emit("shared", "teams");
    }
};

const handleShareViaEmail = async () => {
    if (await shareViaEmail()) {
        trackShare("email");
    }
};

const profileAccessPayload = ref<ProfilePayload | null>(null);
const {
    execute: executeToggleAccess,
    error: accessChangeError,
    isFetching: isAccessChanging,
    statusCode: accessChangeStatus,
} = useFetch("/api/accounts/profile/", { immediate: false }).post(profileAccessPayload).json();

const accessChangeErrorMessage = computed(() =>
    accessChangeStatus.value === 429 ? "Access change failed, please wait a moment and try again." : "Access change failed, please try again.",
);

const ACCESS_ERROR_TIMEOUT_MS = 5000;
const { start: startAccessErrorTimeout, stop: stopAccessErrorTimeout } = useTimeoutFn(
    () => {
        accessChangeError.value = null;
    },
    ACCESS_ERROR_TIMEOUT_MS,
    { immediate: false },
);

watch(accessChangeError, (error) => {
    if (error) {
        startAccessErrorTimeout();
    } else {
        stopAccessErrorTimeout();
    }
});

watch([open, isInviteOnlyAccessLevel], ([isOpen, isInviteOnly]) => {
    if (isOpen && isInviteOnly) {
        ensureInvitationsLoaded();
    } else if (!isOpen) {
        invitations.value = null;
        invitationLoadError.value = null;
        accessChangeError.value = null;
    }
});

const ACCESS_RANK: Record<string, number> = {
    [ACCESS.PRIVATE]: 0,
    [ACCESS.INVITED_ORG_MEMBERS]: 1,
    [ACCESS.ORGANIZATION]: 2,
};

const isReduceAccessConfirmOpen = ref(false);
const pendingAccess = ref<string | null>(null);

const reduceAccessConfirmation = computed(() =>
    pendingAccess.value === ACCESS.PRIVATE
        ? {
              title: "Make your profile private?",
              text: teamsEnabled.value ? t("collaborationProfile.sharing.confirmPrivateTeams") : t("collaborationProfile.sharing.confirmPrivate"),
          }
        : {
              title: "Limit access to invited colleagues?",
              text: teamsEnabled.value ? t("collaborationProfile.sharing.confirmInvitedTeams") : t("collaborationProfile.sharing.confirmInvited"),
          },
);

function restoreSavedAccess(): void {
    localAccess.value = existingPublicProfile.access ?? ACCESS.PRIVATE;
}

function cancelReduceAccess(): void {
    pendingAccess.value = null;
    restoreSavedAccess();
}

// Dismissing via Escape or the overlay closes the dialog without emitting cancel.
watch(isReduceAccessConfirmOpen, (isOpen) => {
    if (!isOpen && pendingAccess.value !== null) {
        cancelReduceAccess();
    }
});

async function confirmReduceAccess(): Promise<void> {
    const newShared = pendingAccess.value;
    pendingAccess.value = null;
    if (newShared) {
        await applyProfileAccess(newShared);
    }
}

function isReducingAccess(newShared: string): boolean {
    const current = existingPublicProfile.access ?? ACCESS.PRIVATE;
    return (ACCESS_RANK[newShared] ?? 0) < (ACCESS_RANK[current] ?? 0);
}

async function handleChangeProfileAccess(newShared: string): Promise<void> {
    if (!existingPublicProfile || existingPublicProfile.access === newShared) {
        return;
    }
    if (isReducingAccess(newShared)) {
        pendingAccess.value = newShared;
        isReduceAccessConfirmOpen.value = true;
        return;
    }
    await applyProfileAccess(newShared);
}

async function applyProfileAccess(newShared: string): Promise<void> {
    hideAccepted();
    accessChangeError.value = null;

    profileAccessPayload.value = {
        name: existingPublicProfile.name ?? "",
        title: existingPublicProfile.title ?? "",
        division: existingPublicProfile.division ?? "",
        is_manager: existingPublicProfile.is_manager ?? false,
        access: newShared,
    };

    await executeToggleAccess();

    if (accessChangeError.value) {
        restoreSavedAccess();
    } else {
        void logUserInteraction(AmplitudeEventNames.PUBLIC_PROFILE_VISIBILITY_CHANGED, {
            new_access: newShared,
            is_shared: newShared === ACCESS.ORGANIZATION,
        });
        showAccepted();
        router.reload({ only: ["existing_public_profile", "viewer_public_profile", "available_access_levels", "share_back_enabled"] });
    }
}

const isButtonDisabled = computed(() => isGeneratingLink.value || isAccessChanging.value);

const { isShowing: isShowingAccepted, show: showAccepted, hide: hideAccepted } = useTimedShow();
</script>
